import React, { useState } from 'react';
import { ChevronDown, Clock, BookOpen, CheckCircle2 } from 'lucide-react';
import { syllabus } from '../data/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const SyllabusSection = () => {
  const [expandedModule, setExpandedModule] = useState(1);
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.1 });

  const toggleModule = (moduleId) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  const totalDuration = syllabus.reduce((acc, mod) => {
    const weeks = parseInt(mod.duration);
    return acc + weeks;
  }, 0);

  return (
    <section id="syllabus" className="syllabus-section" data-testid="syllabus-section">
      <div className="container">
        <div 
          ref={headerRef}
          className={`section-header animate-slide-up ${headerVisible ? 'is-visible' : ''}`}
        >
          <span className="section-tag">Comprehensive Curriculum</span>
          <h2 className="section-title">What You'll Master</h2>
          <p className="section-subtitle">
            {syllabus.length} modules · {totalDuration} weeks · 120+ hours of content
          </p>
        </div>

        <div ref={contentRef} className="syllabus-grid">
          <div className="syllabus-list">
            {syllabus.map((module, index) => (
              <div 
                key={module.id}
                className={`syllabus-item card-animated animate-slide-up ${contentVisible ? 'is-visible' : ''} ${expandedModule === module.id ? 'expanded' : ''}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <button 
                  className="syllabus-header"
                  onClick={() => toggleModule(module.id)}
                  aria-expanded={expandedModule === module.id}
                  data-testid={`syllabus-module-${module.id}`}
                >
                  <div className="syllabus-header-left">
                    <span className="module-number">{module.module}</span>
                    <div className="module-header-info">
                      <h3 className="module-name">{module.title}</h3>
                      <span className="module-meta">
                        <Clock size={14} />
                        {module.duration}
                        <span className="meta-divider">·</span>
                        <BookOpen size={14} />
                        {module.topics.length} topics
                      </span>
                    </div>
                  </div>
                  <ChevronDown 
                    className={`chevron-icon ${expandedModule === module.id ? 'rotated' : ''}`}
                  />
                </button>

                <div className={`syllabus-content ${expandedModule === module.id ? 'open' : ''}`}>
                  <ul className="topics-list">
                    {module.topics.map((topic, topicIndex) => (
                      <li key={topicIndex} className="topic-item">
                        <CheckCircle2 className="topic-icon" />
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className={`syllabus-sidebar animate-slide-right ${contentVisible ? 'is-visible' : ''}`}>
            <div className="sidebar-card hover-lift">
              <h4 className="sidebar-title">Course Highlights</h4>
              <ul className="sidebar-list">
                <li>
                  <span className="sidebar-stat">25+</span>
                  <span className="sidebar-label">Hands-on Projects</span>
                </li>
                <li>
                  <span className="sidebar-stat">120+</span>
                  <span className="sidebar-label">Hours of Content</span>
                </li>
                <li>
                  <span className="sidebar-stat">8</span>
                  <span className="sidebar-label">Comprehensive Modules</span>
                </li>
                <li>
                  <span className="sidebar-stat">Lifetime</span>
                  <span className="sidebar-label">Access & Updates</span>
                </li>
              </ul>
            </div>

            <div className="sidebar-card skills-card hover-lift">
              <h4 className="sidebar-title">Skills You'll Gain</h4>
              <div className="skills-tags">
                <span className="skill-tag hover-scale">Python</span>
                <span className="skill-tag hover-scale">TensorFlow</span>
                <span className="skill-tag hover-scale">PyTorch</span>
                <span className="skill-tag hover-scale">NLP</span>
                <span className="skill-tag hover-scale">Computer Vision</span>
                <span className="skill-tag hover-scale">Deep Learning</span>
                <span className="skill-tag hover-scale">MLOps</span>
                <span className="skill-tag hover-scale">Data Science</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SyllabusSection;
